import { useStyletron } from 'baseui';
import { Button, KIND as BUTTON_KIND, SHAPE as BUTTON_SHAPE } from 'baseui/button';
import { Card, CardProps, StyledBody } from 'baseui/card';
import { ALIGNMENT, BEHAVIOR, Cell, Grid } from 'baseui/layout-grid';
import { Modal, ModalHeader, ModalBody, ModalFooter, ModalButton, ROLE } from 'baseui/modal';
import { NumberedStep, ProgressSteps } from 'baseui/progress-steps';
import { Tag, SIZE as TAG_SIZE, VARIANT } from 'baseui/tag';
import { ACCESSIBILITY_TYPE, PLACEMENT, StatefulTooltip } from 'baseui/tooltip';
import { ComponentChildren, Fragment, h } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import useLocalStorage from '../utils/hooks/useLocalStorage';

const VERSION = process.env.VERSION;

export default function InstructionsModal() {
    const [css, theme] = useStyletron();
    const [hasReadInstructions, setHasReadInstructions] = useLocalStorage<string>('instr');
    const [modalIsOpen, setModalIsOpen] = useState<boolean>(hasReadInstructions != VERSION);
    const close = useMemo(() => () => {
        setHasReadInstructions(VERSION);
        setModalIsOpen(false);
    }, [setHasReadInstructions, setModalIsOpen]);

    // TODO: tooltip does not animate correctly: it appears @ 0,0 and has 0 opacity... maybe a `display: flex` thing?
    return (<>
        <StatefulTooltip accessibilityType={ACCESSIBILITY_TYPE.tooltip}
                         autoFocus={false}
                         content="see the instructions"
                         ignoreBoundary
                         returnFocus={false}>
            <Button disabled={modalIsOpen}
                    kind={BUTTON_KIND.tertiary}
                    onClick={() => setModalIsOpen(true)}
                    shape={BUTTON_SHAPE.circle}>
                ?
            </Button>
        </StatefulTooltip>
        <Modal animate
               autoFocus={false}
               closeable
               isOpen={modalIsOpen}
               onClose={close}
               role={ROLE.dialog}
               overrides={{
                   Dialog: {
                       style: {
                           marginBottom: theme.sizing.scale1200,
                           marginLeft: theme.sizing.scale1200,
                           marginRight: theme.sizing.scale1200,
                           marginTop: theme.sizing.scale1200,
                           width: '80vw',
                       },
                   },
               }}>
            <ModalHeader>How to Use <strong>Coffee Route</strong></ModalHeader>
            <ModalBody>
                <p>In just a few quick steps you can craft yourself a new café hopping experience!</p>
                <ProgressSteps current={0} overrides={{ Root: { style: { maxWidth: '100%' } } }}>
                    <NumberedStep isActive={true} title="Decide you don't want to work at home today">
                        Staying at home all day is a drag and going to the office is a chore, so grab your laptop and
                        hit the streets: visit a few cafés, have a coffee, take in the ambience, let the sounds of
                        others' dispassionate Zoom meetings and pained typing drown out your own existential dread born
                        from working in a late-stage capitalist hellhole, grab a bite, meet your friends, engage in
                        productivity!
                    </NumberedStep>
                    <NumberedStep isActive={true} title="Describe your desired experience">
                        <Grid align={ALIGNMENT.center} behavior={BEHAVIOR.fluid} overrides={{
                            Grid: {
                                style: {
                                    alignContent: 'space-between',
                                    alignItems: 'flex-start !important',
                                    height: '100%',
                                    rowGap: '36px',
                                },
                            },
                            GridWrapper: { style: { height: '100%' } },
                        }}>
                            <Cell span={[12, 6, 6]}>
                                <VideoCard headerVideo="/assets/select_preferences_sd.webm"
                                           title="Select your preferences">
                                    <StyledBody>
                                        Use the preferences form to describe the kind of coffee routes you'd be
                                        interested in; we'll check to make sure that the cafés on your route are open
                                        during the time window you select, and you can also specify if they must have
                                        wifi.
                                    </StyledBody>
                                </VideoCard>
                            </Cell>
                            <Cell span={[12, 6, 6]}>
                                <VideoCard headerVideo="/assets/select_location_sd.webm"
                                           title="Select a search area">
                                    <StyledBody>
                                        Drop a pin on the map by clicking on it, or by pressing the geolocation button.
                                        Drag the pin to your desired starting point, and use the small white handle on
                                        the edge of the search circle to adjust its radius.
                                    </StyledBody>
                                </VideoCard>
                            </Cell>
                        </Grid>
                    </NumberedStep>
                    <NumberedStep isActive={true} title="We find you a route">
                        Once you've selected a location and your preferences, click the
                        <InlineFakeButton>Submit</InlineFakeButton> button and we'll brew up a route for you! This can
                        take a up to a few seconds as we search for just the right places to show you.
                        <img src="../assets/generated_coffee_route.png" alt="Generated Coffee Route" className={css({
                            marginTop: theme.sizing.scale300,
                            maxWidth: 'min(100%, 600px)',
                        })} />
                    </NumberedStep>
                    <NumberedStep isActive={true} title="Bon voyage!">
                        Get to café hopping! We also provide a link to see your route on Google Maps: just click on the
                        <InlineFakeButton>See route on Google</InlineFakeButton> button and then select your preferred
                        mode of transportation, as you normally would (note: we are not affiliated with Google Maps or
                        Google at all, we just think it's convenient to be able to see your route that way).
                        <br /><br />
                        Or, if you don't like the route, you can click the
                        <InlineFakeButton>New search</InlineFakeButton> button to go back and plan a new one.
                    </NumberedStep>
                </ProgressSteps>
            </ModalBody>
            <ModalFooter>
                <ModalButton onClick={close}>Get started</ModalButton>
            </ModalFooter>
        </Modal>
    </>);
}

function InlineFakeButton({ children }: { children: ComponentChildren }) {
    return (
        <Tag closeable={false} size={TAG_SIZE.small} variant={VARIANT.solid} overrides={{
            Root: { style: { display: "inline-flex", marginBottom: 0, marginTop: 0, } }
        }}>{children}</Tag>
    );
}

function VideoCard(props: Omit<CardProps, 'headerImage' | 'overrides'> & {
    headerVideo: string;
    overrides?: CardProps['overrides']
}) {
    const [css] = useStyletron();
    return <Card {...props} headerImage={props.headerVideo} overrides={{
        ...(props?.overrides || {}),
        HeaderImage: {
            component: () => (<video autoPlay className={css({ width: '100%' })} loop muted playsInline src={props.headerVideo} />),
        },
    }}>{props?.children}</Card>;
}
